import * as types from "./FullLengthExamTypes";

const initialState = {
  // Admin - Exam Templates
  exams: [],
  currentExam: null,
  examsLoading: false,
  examLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  // Student - Exam Attempts
  attempts: [],
  currentAttempt: null,
  attemptsLoading: false,
  attemptLoading: false,
  saveLoading: false,
  submitLoading: false,

  // Admin - All Exam Attempts (analytics)
  allAttempts: [],
  allAttemptsLoading: false,

  // Admin - Per-Exam Question Analytics
  examQuestionAnalytics: null,
  examQuestionAnalyticsLoading: false,

  // Exam History (for analytics)
  examHistory: [],
  examHistoryLoading: false,

  // Admin - Enhanced Analytics (with user profiles)
  enhancedAnalytics: null,
  enhancedAnalyticsLoading: false,

  // Admin - Student Exam Details (for modal)
  studentExamDetails: null,
  studentExamDetailsLoading: false,

  // Admin - Attempt Counts
  attemptCounts: {},
  attemptCountsLoading: false,

  // Common
  error: null,
  success: false,
  lastVisible: null,
  hasMore: true,
};

const fullLengthExamReducer = (state = initialState, action) => {
  switch (action.type) {
    // ===========================
    // ADMIN - EXAM TEMPLATE CRUD
    // ===========================

    // Create Exam
    case types.CREATE_EXAM_REQUEST:
      return {
        ...state,
        createLoading: true,
        error: null,
        success: false,
      };
    case types.CREATE_EXAM_SUCCESS:
      return {
        ...state,
        createLoading: false,
        exams: [action.payload, ...state.exams],
        currentExam: action.payload,
        success: true,
      };
    case types.CREATE_EXAM_FAILURE:
      return {
        ...state,
        createLoading: false,
        error: action.payload,
        success: false,
      };

    // Fetch All Exams
    case types.FETCH_EXAMS_REQUEST:
      return {
        ...state,
        examsLoading: true,
        error: null,
      };
    case types.FETCH_EXAMS_SUCCESS:
      return {
        ...state,
        examsLoading: false,
        exams: action.payload.append
          ? [...state.exams, ...action.payload.exams]
          : action.payload.exams,
        lastVisible: action.payload.lastVisible,
        hasMore: action.payload.hasMore,
      };
    case types.FETCH_EXAMS_FAILURE:
      return {
        ...state,
        examsLoading: false,
        error: action.payload,
      };

    // Fetch Single Exam
    case types.FETCH_EXAM_REQUEST:
      return {
        ...state,
        examLoading: true,
        error: null,
      };
    case types.FETCH_EXAM_SUCCESS:
      return {
        ...state,
        examLoading: false,
        currentExam: action.payload,
      };
    case types.FETCH_EXAM_FAILURE:
      return {
        ...state,
        examLoading: false,
        error: action.payload,
      };

    // Update Exam
    case types.UPDATE_EXAM_REQUEST:
      return {
        ...state,
        updateLoading: true,
        error: null,
        success: false,
      };
    case types.UPDATE_EXAM_SUCCESS:
      return {
        ...state,
        updateLoading: false,
        exams: state.exams.map((exam) =>
          exam.examId === action.payload.examId ? action.payload : exam
        ),
        currentExam: action.payload,
        success: true,
      };
    case types.UPDATE_EXAM_FAILURE:
      return {
        ...state,
        updateLoading: false,
        error: action.payload,
        success: false,
      };

    // Delete Exam
    case types.DELETE_EXAM_REQUEST:
      return {
        ...state,
        deleteLoading: true,
        error: null,
      };
    case types.DELETE_EXAM_SUCCESS:
      return {
        ...state,
        deleteLoading: false,
        exams: state.exams.filter((exam) => exam.examId !== action.payload),
        currentExam: state.currentExam?.examId === action.payload ? null : state.currentExam,
      };
    case types.DELETE_EXAM_FAILURE:
      return {
        ...state,
        deleteLoading: false,
        error: action.payload,
      };

    // ===========================
    // STUDENT - EXAM ATTEMPTS
    // ===========================

    // Create Exam Attempt
    case types.CREATE_EXAM_ATTEMPT_REQUEST:
      return {
        ...state,
        attemptLoading: true,
        error: null,
        success: false,
      };
    case types.CREATE_EXAM_ATTEMPT_SUCCESS:
      return {
        ...state,
        attemptLoading: false,
        attempts: [action.payload, ...state.attempts],
        currentAttempt: action.payload,
        success: true,
      };
    case types.CREATE_EXAM_ATTEMPT_FAILURE:
      return {
        ...state,
        attemptLoading: false,
        error: action.payload,
        success: false,
      };

    // Fetch All Attempts
    case types.FETCH_EXAM_ATTEMPTS_REQUEST:
      return {
        ...state,
        attemptsLoading: true,
        error: null,
      };
    case types.FETCH_EXAM_ATTEMPTS_SUCCESS:
      return {
        ...state,
        attemptsLoading: false,
        attempts: action.payload.append
          ? [...state.attempts, ...action.payload.attempts]
          : action.payload.attempts,
        lastVisible: action.payload.lastVisible,
        hasMore: action.payload.hasMore,
      };
    case types.FETCH_EXAM_ATTEMPTS_FAILURE:
      return {
        ...state,
        attemptsLoading: false,
        error: action.payload,
      };

    // Fetch Single Attempt
    case types.FETCH_EXAM_ATTEMPT_REQUEST:
      return {
        ...state,
        attemptLoading: true,
        error: null,
      };
    case types.FETCH_EXAM_ATTEMPT_SUCCESS:
      return {
        ...state,
        attemptLoading: false,
        currentAttempt: action.payload,
      };
    case types.FETCH_EXAM_ATTEMPT_FAILURE:
      return {
        ...state,
        attemptLoading: false,
        error: action.payload,
      };

    // Update Exam Attempt
    case types.UPDATE_EXAM_ATTEMPT_REQUEST:
      return {
        ...state,
        attemptLoading: true,
        error: null,
      };
    case types.UPDATE_EXAM_ATTEMPT_SUCCESS:
      return {
        ...state,
        attemptLoading: false,
        attempts: state.attempts.map((attempt) =>
          attempt.attemptId === action.payload.attemptId ? action.payload : attempt
        ),
        currentAttempt: action.payload,
      };
    case types.UPDATE_EXAM_ATTEMPT_FAILURE:
      return {
        ...state,
        attemptLoading: false,
        error: action.payload,
      };

    // Save Exam Progress (auto-save)
    case types.SAVE_EXAM_PROGRESS_REQUEST:
      return {
        ...state,
        saveLoading: true,
        error: null,
      };
    case types.SAVE_EXAM_PROGRESS_SUCCESS:
      return {
        ...state,
        saveLoading: false,
        currentAttempt: action.payload,
      };
    case types.SAVE_EXAM_PROGRESS_FAILURE:
      return {
        ...state,
        saveLoading: false,
        error: action.payload,
      };

    // Submit Exam
    case types.SUBMIT_EXAM_REQUEST:
      return {
        ...state,
        submitLoading: true,
        error: null,
      };
    case types.SUBMIT_EXAM_SUCCESS:
      return {
        ...state,
        submitLoading: false,
        currentAttempt: action.payload,
        attempts: state.attempts.map((attempt) =>
          attempt.attemptId === action.payload.attemptId ? action.payload : attempt
        ),
        success: true,
      };
    case types.SUBMIT_EXAM_FAILURE:
      return {
        ...state,
        submitLoading: false,
        error: action.payload,
      };

    // ===========================
    // ADMIN - ALL EXAM ATTEMPTS (analytics)
    // ===========================
    case types.FETCH_ALL_EXAM_ATTEMPTS_REQUEST:
      return {
        ...state,
        allAttemptsLoading: true,
        error: null,
      };
    case types.FETCH_ALL_EXAM_ATTEMPTS_SUCCESS:
      return {
        ...state,
        allAttemptsLoading: false,
        allAttempts: action.payload,
      };
    case types.FETCH_ALL_EXAM_ATTEMPTS_FAILURE:
      return {
        ...state,
        allAttemptsLoading: false,
        error: action.payload,
      };

    // ===========================
    // ADMIN - PER-EXAM QUESTION ANALYTICS
    // ===========================
    case types.FETCH_EXAM_QUESTION_ANALYTICS_REQUEST:
      return {
        ...state,
        examQuestionAnalyticsLoading: true,
        error: null,
      };
    case types.FETCH_EXAM_QUESTION_ANALYTICS_SUCCESS:
      return {
        ...state,
        examQuestionAnalyticsLoading: false,
        examQuestionAnalytics: action.payload,
      };
    case types.FETCH_EXAM_QUESTION_ANALYTICS_FAILURE:
      return {
        ...state,
        examQuestionAnalyticsLoading: false,
        error: action.payload,
      };

    // ===========================
    // EXAM HISTORY (for analytics)
    // ===========================
    case types.FETCH_EXAM_HISTORY_REQUEST:
      return {
        ...state,
        examHistoryLoading: true,
        error: null,
      };
    case types.FETCH_EXAM_HISTORY_SUCCESS:
      return {
        ...state,
        examHistoryLoading: false,
        examHistory: action.payload,
      };
    case types.FETCH_EXAM_HISTORY_FAILURE:
      return {
        ...state,
        examHistoryLoading: false,
        error: action.payload,
      };

    // ===========================
    // ADMIN - ENHANCED ANALYTICS
    // ===========================
    case types.FETCH_ENHANCED_ANALYTICS_REQUEST:
      return { ...state, enhancedAnalyticsLoading: true, error: null };
    case types.FETCH_ENHANCED_ANALYTICS_SUCCESS:
      return { ...state, enhancedAnalyticsLoading: false, enhancedAnalytics: action.payload };
    case types.FETCH_ENHANCED_ANALYTICS_FAILURE:
      return { ...state, enhancedAnalyticsLoading: false, error: action.payload };

    // ===========================
    // ADMIN - STUDENT EXAM DETAILS
    // ===========================
    case types.FETCH_STUDENT_EXAM_DETAILS_REQUEST:
      return { ...state, studentExamDetailsLoading: true, error: null };
    case types.FETCH_STUDENT_EXAM_DETAILS_SUCCESS:
      return { ...state, studentExamDetailsLoading: false, studentExamDetails: action.payload };
    case types.FETCH_STUDENT_EXAM_DETAILS_FAILURE:
      return { ...state, studentExamDetailsLoading: false, error: action.payload };

    // ===========================
    // ADMIN - ATTEMPT COUNTS
    // ===========================
    case types.FETCH_EXAM_ATTEMPT_COUNTS_SUCCESS:
      return { ...state, attemptCountsLoading: false, attemptCounts: action.payload };

    // ===========================
    // ADMIN - ARCHIVE EXAM
    // ===========================
    case types.ARCHIVE_EXAM_REQUEST:
      return { ...state, deleteLoading: true, error: null };
    case types.ARCHIVE_EXAM_SUCCESS:
      return {
        ...state,
        deleteLoading: false,
        exams: state.exams.filter((exam) => exam.examId !== action.payload),
        currentExam: state.currentExam?.examId === action.payload ? null : state.currentExam,
      };
    case types.ARCHIVE_EXAM_FAILURE:
      return { ...state, deleteLoading: false, error: action.payload };

    // ===========================
    // CLEAR/RESET
    // ===========================
    case types.CLEAR_ENHANCED_ANALYTICS:
      return { ...state, enhancedAnalytics: null, error: null };
    case types.CLEAR_STUDENT_EXAM_DETAILS:
      return { ...state, studentExamDetails: null, error: null };
    case types.CLEAR_CURRENT_EXAM:
      return {
        ...state,
        currentExam: null,
        error: null,
        success: false,
      };
    case types.CLEAR_CURRENT_ATTEMPT:
      return {
        ...state,
        currentAttempt: null,
        error: null,
        success: false,
      };
    case types.CLEAR_EXAM_HISTORY:
      return {
        ...state,
        examHistory: [],
        error: null,
      };
    case types.RESET_EXAM_STATE:
      return initialState;

    default:
      return state;
  }
};

export default fullLengthExamReducer;
